import React, { useState } from 'react'
import { Button, Form, AnglePlanBadge } from 'react-angle-dashboard-components'
import styled, { keyframes } from 'styled-components'
import { ISelectablePlan } from '../interfaces/plan'
import { Paths } from '../routes/paths'
import { BenefitsModal } from './BenefitsModal'

const computeBorders = (selected?: boolean, left?: boolean) => {
  if (selected) return ''

  const variableBorder = left ? 'right' : 'left'

  return `border-bottom: 1px solid #DEE2E6; border-${variableBorder}: 1px solid #DEE2E6;`
}

export const PlanWrapper = styled.div<{
  selected?: boolean
  nonSelectedOpacity?: number
}>`
  border: ${(props) =>
    props.selected ? '1px solid var(--primary);' : '0.88px solid #dee2e6;'}
    ${(props) =>
      !props.selected ? `opacity: ${props.nonSelectedOpacity};` : '1;'}
  width: 280px;
  height: 590px;
  border-radius: 4.37799px;
  margin-bottom: 15px;
  position: relative;
  background-color: #fff;
  box-shadow: 5px 5px 15px 5px rgba(0, 0, 0, 3%);
  user-select: none;
  cursor: pointer;
  @media (min-width: 1024px) {
    margin: 1em 2rem 1em 0em;
  }
`

const TabButton = styled.button<{ left?: boolean; selected?: boolean }>`
  background: none;
  font-family: inherit;
  font-size: 14px;
  transition: width 0.06s ease-in;
  outline: none;
  border: none;
  height: 45px;
  width: ${(props) => (props.selected ? '49%' : '51%')};
  ${(props) => (props.selected ? 'font-weight: 600;' : '')}
  ${(props) => computeBorders(props.selected, props.left)}
`

const Selector = styled(Form.Check)`
  position: absolute;
  top: 11px;
  left: 15px;
`

const fromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-15px);
  }

  to {
    opacity: 1;
    transform: translateX(0px);
  }
`

const CostContent = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  animation: ${fromLeft} 0.06s linear;
`

const OverviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-family: inherit;
  padding: 18px;
  border-bottom: 1px solid #dee2e6;
  margin: 0 17px;
`

const PlanName = styled.span`
  margin: 0;
  padding: 0;
  font-weight: 500;
  font-size: 18px;
  margin-bottom: 4px;
`

const PlanPrice = styled.span`
  font-size: 26px;
  color: var(--primary);
  font-weight: 700;
`

const PlanSuffix = styled.span`
  font-size: 19px;
  color: var(--secondary);
`

const MonthlyCost = styled.div`
  padding: 18px;
  border-bottom: 1px solid #dee2e6;
`

const Subtitle = styled.p`
  margin: 0 0 8px 0;
  padding: 0;
  font-size: 12px;
  color: var(--secondary);
`

const DataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`

const AvgPrice = styled.span`
  font-weight: 600;
  font-size: 17px;
`

const AvgSuffix = styled.span`
  font-size: 14px;
  color: var(--secondary);
`

const DataLabel = styled.span`
  font-size: 14px;
`

const OOPWrapper = styled.div`
  padding: 18px 18px 14px 18px;
`

const OOPValue = styled.span`
  font-weight: 600;
  font-size: 14px;
`

const FindLink = styled.a`
  color: var(--primary);
  text-decoration: underline;
  font-size: 14px;
  margin-left: 18px;
  cursor: pointer;
`

const ButtonWrapper = styled.div`
  padding: 0 18px;
`

const SeeFullPlanButton = styled(Button)`
  width: 100%;
  font-size: 14px;
  box-sizing: border-box;
  height: 35px;
  padding: 0;
  margin-top: 10px;
`

interface PlanInformationProps {
  plan: ISelectablePlan
  onPlanSelect: (selected: boolean, id: string) => void
  nonSelectedOpacity: number
  onShowPlanDetails: (id: string, plan: any) => void
}

export const CostInformation: React.FC<{
  plan: ISelectablePlan
  compressedVersion?: boolean
  onShowPlanDetails?: (id: string, plan: any) => void
  showFullPlanDetails?: boolean
}> = ({
  plan,
  compressedVersion,
  onShowPlanDetails = () => {},
  showFullPlanDetails = true
}) => (
  <CostContent>
    <OverviewWrapper>
      <PlanName
        style={{
          marginLeft: -10
        }}
      >
        <AnglePlanBadge metalTier={plan.tier} />
        {plan.name}
      </PlanName>
      <div>
        <PlanPrice>${plan.employerCostPerEmployee?.toLocaleString()}</PlanPrice>
        <PlanSuffix>/mo</PlanSuffix>
      </div>
      <p className="fs-14" style={{ marginBottom: 5 }}>
        Employer cost
      </p>
    </OverviewWrapper>
    <MonthlyCost>
      <Subtitle>Avg. monthly cost per employee</Subtitle>
      <DataWrapper>
        <div>
          <AvgPrice>${plan.employeeCostPerEmployee}</AvgPrice>
          <AvgSuffix>/mo</AvgSuffix>
        </div>
        <DataLabel>Employee cost</DataLabel>
      </DataWrapper>
      <DataWrapper>
        <div>
          <AvgPrice>${plan.totalCostPerEmployee}</AvgPrice>
          <AvgSuffix>/mo</AvgSuffix>
        </div>
        <DataLabel>Total cost</DataLabel>
      </DataWrapper>
    </MonthlyCost>
    <OOPWrapper>
      <Subtitle>In-Network Costs</Subtitle>
      <DataWrapper>
        <OOPValue>${plan.maxOOP.toLocaleString()}</OOPValue>
        <DataLabel>Max Out-of-pocket</DataLabel>
      </DataWrapper>
      <DataWrapper>
        <OOPValue>${plan.deductible.toLocaleString()}</OOPValue>
        <DataLabel>Deductible</DataLabel>
      </DataWrapper>
      <DataWrapper>
        <OOPValue>${plan.doctorVisitCopay.toLocaleString()}</OOPValue>
        <DataLabel>Primary Doctor Copay</DataLabel>
      </DataWrapper>
    </OOPWrapper>
    {!compressedVersion && (
      <FindLink href={Paths.publicProviders} target="_blank">
        Find your doctor
      </FindLink>
    )}

    <ButtonWrapper className="btn-wrapper-benefits">
      <SeeFullPlanButton
        onClick={(e: any) => {
          e.stopPropagation()
          onShowPlanDetails(plan.id, plan)
        }}
        variant="primary"
      >
        See Full Plan Details
      </SeeFullPlanButton>
      <p className="text-secondary fs-12 mt-2">
        Health insurance plan underwritten by Angle Insurance Company of Utah
      </p>
    </ButtonWrapper>
  </CostContent>
)

export const PlanInformation: React.FC<PlanInformationProps> = ({
  plan,
  onPlanSelect,
  onShowPlanDetails,
  nonSelectedOpacity = 1
}) => {
  const [displayBenefits, setDisplayBenefits] = useState(false)

  return (
    <PlanWrapper
      selected={plan.selected}
      nonSelectedOpacity={nonSelectedOpacity}
      onClick={() => onPlanSelect(!plan.selected, plan.id)}
    >
      <BenefitsModal
        show={displayBenefits}
        onHide={() => setDisplayBenefits(false)}
        plan={plan}
      />
      <Selector
        type="checkbox"
        checked={plan.selected}
        onChange={(e: any) => {
          onPlanSelect(e.target.checked, plan.id)
        }}
      />
      <TabButton
        left
        selected={!displayBenefits}
        onClick={() => setDisplayBenefits(false)}
      >
        Plan Costs
      </TabButton>
      <TabButton
        selected={displayBenefits}
        onClick={(e: any) => {
          e.stopPropagation()
          setDisplayBenefits(true)
        }}
      >
        Benefit Details
      </TabButton>
      <CostInformation plan={plan} onShowPlanDetails={onShowPlanDetails} />
    </PlanWrapper>
  )
}

export default PlanInformation
